import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Facebook, Menu, X, Info, Star, ShieldCheck, HardHat, Award, Landmark } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Algon Engineering Construction Corporation — concept site. Established 1966;
  corporate/infrastructure identity in the company's own yellow/black/red, built
  on real bridge, warehouse and institutional project photography.
*/

const YELLOW = "#F2B705";
const BLACK = "#141414";
const RED = "#C81E2C";
const CREAM = "#F7F5EE";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/algonengineering/";
const NAME = "Algon Engineering Construction Corporation";
const SLUG = "algon-engineering-construction-concept";
const P = "/construction/algon";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function AlgonConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Capabilities", "#capabilities"], ["Projects", "#projects"], ["Quality & Safety", "#quality"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Roboto Condensed', sans-serif" }} data-testid="algon-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;500;600;700&family=Oswald:wght@500;600;700&display=swap');
        .ag-head { font-family: 'Oswald', sans-serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-black transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.3)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="ag-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.png`} alt={NAME} className="h-11 w-11 object-contain" />
            <span className="ag-head text-lg text-white tracking-wide">ALGON</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold uppercase tracking-wide text-white/75 hover:text-white">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="ag-header-cta" className="hidden sm:inline-flex px-5 py-2.5 text-sm font-bold uppercase" style={{ background: YELLOW, color: BLACK }}>
            Request a Proposal
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="ag-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-black">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white font-semibold uppercase">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="ag-mobile-cta" className="block mt-2 text-center px-5 py-3 text-sm font-bold uppercase" style={{ background: YELLOW, color: BLACK }}>Request a Proposal</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[84vh] flex items-end" data-testid="ag-hero">
        <img src={`${P}/bridge-infrastructure.jpg`} alt="Bridge infrastructure work by Algon Engineering Construction" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,20,20,0.15) 0%, rgba(20,20,20,0.65) 60%, rgba(20,20,20,0.95) 100%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: YELLOW }}>Est. 1966 · Davao City</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-tight text-white ag-head font-bold uppercase">Strength Built on <span style={{ color: RED }}>Trust.</span></h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
              Nearly six decades of general engineering and construction — infrastructure, industrial and institutional projects delivered with a commitment to quality, workmanship and safety.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="ag-hero-cta" className="px-7 py-3.5 text-sm font-bold uppercase" style={{ background: YELLOW, color: BLACK }}>Request a Proposal</a>
              <a href="#projects" data-testid="ag-hero-projects" className="border border-white/40 px-7 py-3.5 text-sm font-bold uppercase text-white hover:bg-white/10 transition-colors">Our Projects</a>
            </div>
            <p className="mt-7 text-sm text-white/70" data-testid="ag-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: YELLOW }} /> 4.9 Google Rating · 8 Reviews · Davao City
            </p>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>What We Do</span>
          <h2 className="mt-3 text-4xl sm:text-5xl ag-head font-bold uppercase leading-snug">Capabilities</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [Landmark, "Horizontal Infrastructure", "Roads, highways, bridges, railways, airports, ports, flood control, water supply and sewerage works."],
            [HardHat, "Industrial & Institutional", "Warehouses, institutional buildings, ready-mix concrete, asphalt and recreational works."],
            [ShieldCheck, "Safety & Quality Systems", "Prompt client satisfaction, workmanship and employee safety guide every project we run."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: RED }} />
              <h3 className="mt-4 text-lg font-bold ag-head uppercase">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-20 md:py-28" style={{ background: BLACK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: YELLOW }}>On Site</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white ag-head font-bold uppercase leading-snug">Our Projects</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { img: `${P}/bridge-infrastructure.jpg`, label: "Bridge / road infrastructure work" },
              { img: `${P}/resort-amenity.jpg`, label: "Institutional / resort amenity project" },
              { img: `${P}/warehouse-team.jpg`, label: "8-unit warehouse project, structural stage" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="ag-project-item" className="overflow-hidden">
                <img src={it.img} alt={it.label} className="w-full h-64 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/70">{it.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUALITY & SAFETY */}
      <section id="quality" className="py-16 md:py-20" style={{ background: "#EFE8D4" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp}>
            <Award size={28} className="mx-auto" style={{ color: RED }} />
            <p className="mt-5 text-lg leading-relaxed" style={{ color: `${INK}90` }}>
              Recognized by the University of Mindanao's College of Engineering Education as a Host Training Establishment for on-the-job engineering training — one part of our ongoing commitment to developing the next generation of engineers.
            </p>
            <p className="mt-5 text-sm leading-relaxed" style={{ color: `${INK}AA` }}>
              Government performance records include two Davao–Cotabato road projects evaluated at 89.30 and 89.80, plus a DOST laboratory-building project with a 97.08% “Outstanding” final rating.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl ag-head font-bold uppercase leading-snug">Request a Proposal</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>Algon Building, 159 J.P. Cabaguio Ave., Agdao, Davao City</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ag-facebook-link" className="hover:underline">facebook.com/algonengineering</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="ag"
              accent={RED}
              accentText="#FFFFFF"
              heading="Request a Proposal"
              subheading="Infrastructure, industrial or institutional — tell us about the project and our team will follow up."
              projectTypes={["Infrastructure / Civil Works", "Industrial Facility", "Institutional Building", "Other"]}
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: BLACK, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.png`} alt={NAME} className="h-10 w-10 object-contain" />
              <span className="text-white text-lg ag-head font-bold">ALGON ENGINEERING</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">General engineering construction in Davao City since 1966 — infrastructure, industrial and institutional work.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Algon Building, 159 J.P. Cabaguio Ave.,<br />Agdao, Davao City</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Licenses, certifications and project details should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-bold uppercase text-black shadow-xl" style={{ background: YELLOW }}>
        Message Algon
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
